import React, { useState } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { useSearchBox, useHits, useInstantSearch } from 'react-instantsearch';
import { useAuth } from '../contexts/AuthContext';
import { searchClient, INDEX_NAME } from '../lib/algolia';
import PaperCard from '../components/PaperCard';
import PaperRow from '../components/PaperRow';
import AudioPlayer from '../components/AudioPlayer';

const SearchBar = ({ viewMode, onViewChange }) => {
  const { query, refine } = useSearchBox();

  return (
    <div className="flex gap-3 items-center">
      <div className="relative flex-1 max-w-2xl">
        <input
          type="text"
          value={query}
          onChange={(e) => refine(e.target.value)}
          placeholder="Search papers, topics, categories…"
          className="w-full pl-12 pr-4 py-3.5 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {query && (
          <button onClick={() => refine('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex items-center border border-gray-300 rounded-xl overflow-hidden">
        <button
          onClick={() => onViewChange('grid')}
          className={`p-3 transition-colors ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          title="Grid view"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
        <button
          onClick={() => onViewChange('list')}
          className={`p-3 transition-colors ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
          title="List view"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 animate-pulse">
    <div className="flex justify-between mb-3">
      <div className="h-6 w-24 bg-gray-200 rounded-full" />
      <div className="h-6 w-16 bg-gray-200 rounded-full" />
    </div>
    <div className="h-5 bg-gray-200 rounded mb-2 w-3/4" />
    <div className="h-4 bg-gray-100 rounded mb-1 w-full" />
    <div className="h-4 bg-gray-100 rounded mb-6 w-2/3" />
    <div className="flex gap-2 mt-4">
      <div className="flex-1 h-9 bg-gray-200 rounded-lg" />
      <div className="w-20 h-9 bg-gray-100 rounded-lg" />
    </div>
  </div>
);

const PaperHits = ({ viewMode, onPlay, onDownload }) => {
  const { hits } = useHits();
  const { status } = useInstantSearch();

  if (status === 'loading' || status === 'stalled') {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 9 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  if (hits.length === 0) {
    return (
      <div className="text-center py-20">
        <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500 text-lg font-medium">No papers found</p>
        <p className="text-gray-400 text-sm mt-1">Try a different search term</p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {hits.map((hit) => (
          <PaperCard key={hit.objectID} hit={hit} onPlay={onPlay} onDownload={onDownload} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {hits.map((hit) => (
        <PaperRow key={hit.objectID} hit={hit} onPlay={onPlay} onDownload={onDownload} />
      ))}
    </div>
  );
};

const TrialBanner = ({ daysLeft }) => (
  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-6">
    <div className="flex items-center gap-3">
      <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
      <span className="text-sm font-medium text-amber-800">
        {daysLeft === 0
          ? 'Your free trial ends today'
          : `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
      </span>
    </div>
    <a href="/account" className="text-sm font-semibold text-amber-700 hover:text-amber-900 transition-colors whitespace-nowrap">
      View plan →
    </a>
  </div>
);

const Dashboard = () => {
  const { currentUser, isTrialing, trialDaysLeft, logout } = useAuth();
  const [viewMode, setViewMode] = useState('grid');
  const [currentAudio, setCurrentAudio] = useState(null);

  const handlePlay = (paper) => setCurrentAudio(paper);

  const handleDownload = (paper) => {
    const a = document.createElement('a');
    a.href = paper.pdfUrl;
    a.download = paper.title + '.pdf';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 stripe-gradient rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" />
              </svg>
            </div>
            <span className="text-2xl font-semibold text-gray-900">Journal Coffee</span>
          </div>
          <div className="flex items-center gap-4">
            {isTrialing && trialDaysLeft !== null && (
              <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                Trial — {trialDaysLeft}d left
              </span>
            )}
            {!isTrialing && (
              <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full">
                Premium
              </span>
            )}
            <a href="/account" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Account
            </a>
            <button onClick={logout} className="bg-black text-white px-5 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main className={`max-w-6xl mx-auto px-6 py-10 ${currentAudio ? 'pb-28' : ''}`}>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Research Library</h1>
          <p className="text-gray-500">Discover and listen to research paper summaries</p>
        </div>

        {isTrialing && trialDaysLeft !== null && <TrialBanner daysLeft={trialDaysLeft} />}

        <InstantSearch searchClient={searchClient} indexName={INDEX_NAME} future={{ preserveSharedStateOnUnmount: true }}>
          <Configure hitsPerPage={24} />
          <div className="mb-6">
            <SearchBar viewMode={viewMode} onViewChange={setViewMode} />
          </div>
          <PaperHits viewMode={viewMode} onPlay={handlePlay} onDownload={handleDownload} />
        </InstantSearch>
      </main>

      {currentAudio && (
        <AudioPlayer currentAudio={currentAudio} onClose={() => setCurrentAudio(null)} />
      )}
    </div>
  );
};

export default Dashboard;
